import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Formik, Form as FormikForm, Field as FormikField } from "formik";

import { useNavigate } from "react-router-dom/dist";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MuiCircularProgress from "@mui/material/CircularProgress";
import LockIcon from "@mui/icons-material/Lock";
import { Link } from "@material-ui/core";
import { redirectTo } from "../utils/redirectTo";

import { config } from "../app/config";
import { useAuth } from "../hooks/useAuth";
import Auth from "../services/auth";
import toastMessage from "../utils/toastMessage";
import { theme } from "../styles/theme";
import { Turnstile } from "../components/Turnstile";

const validationSchema = Yup.object().shape({
  userLogin: Yup.string().required("Campo obrigatório"),
  password: Yup.string().required("Campo obrigatório"),
});

const Login = () => {
  const {
    onCredentialsSubmit,
    userLogin,
    setUserLogin,
    setPasswordLogin,
    isLogin,
    isAuthenticated,
    passwordLogin,
    hasTwoFactorAuth,
    has2FASessionAccess,
  } = useAuth();

  const navigate = useNavigate();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const handleUserRedirect = (basename) => {
    config.all.isDev
      ? redirectTo[config.all.validateEnvironment](basename)
      : redirectTo["PUBLICA"](basename);
  };

  useEffect(() => {
    if (isAuthenticated && hasTwoFactorAuth && has2FASessionAccess) {
      getValidateCode();
      return;
    }

    if (isAuthenticated && !hasTwoFactorAuth) return navigate("/auth/context");

    navigate("/");
  }, [isAuthenticated, navigate, hasTwoFactorAuth, has2FASessionAccess]);

  async function getValidateCode() {
    try {
      const { ok } = await Auth.getValidateCode();

      if (!ok) {
        navigate("/auth/two-factor");
        return;
      }

      navigate("/auth/context");
      return;
    } catch (err) {
      toastMessage.toastMessageError();
    }
  }

  const initialValues = {
    userLogin: userLogin,
    password: passwordLogin,
  };

  const handleSubmit = async () => {
    const ok = await onCredentialsSubmit(turnstileToken);
    if (ok === false) {
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  };

  return (
    <Box
      sx={{
        width: 400,
        backgroundColor: "white",
        margin: "10%",
        padding: "24px",
        borderRadius: "8px",
      }}
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors }) => (
          <FormikForm noValidate>
            <div className="textFieldContainer">
              <FormikField
                disabled={isLogin}
                component={TextField}
                name="userLogin"
                label="Usuário"
                variant="standard"
                fullWidth
                sx={{
                  marginBottom: "16px",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setUserLogin(e.target.value)}
                error={errors.userLogin}
                helperText={errors.userLogin}
              />
              <FormikField
                disabled={isLogin}
                component={TextField}
                name="password"
                label="Senha"
                variant="standard"
                fullWidth
                type="password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setPasswordLogin(e.target.value)}
                error={errors.password}
                helperText={errors.password}
              />
            </div>

            {config.all.turnstileSiteKey && (
              <Box mt={2}>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={config.all.turnstileSiteKey}
                  options={{ theme: "light", size: "flexible" }}
                  onSuccess={setTurnstileToken}
                />
              </Box>
            )}

            <Button
              fullWidth
              type="submit"
              sx={{
                backgroundColor: "#658790",
                color: "white",
                height: 50,
                fontSize: 16,
                marginBottom: "16px",
                marginTop: "16px",
                "&:hover": {
                  backgroundColor: "#4d676e",
                },
              }}
            >
              {isLogin ? (
                <MuiCircularProgress size={30} color="inherit" />
              ) : (
                "ENTRAR"
              )}
            </Button>
            <Button
              disabled={isLogin}
              type="button"
              variant="outlined"
              sx={{
                color: "#658790",
                borderColor: "#658790",
                height: 36,
                fontSize: 12,
                width: "100%",
                "&:hover": {
                  borderColor: "#4d676e",
                  backgroundColor: "transparent",
                },
              }}
              onClick={() => handleUserRedirect("auth/e-cpf")}
            >
              LOGAR COM E-CPF
            </Button>

            <Box component="footer" display="flex" alignItems="left">
              <Button
                type="button"
                disabled={isLogin}
                onClick={() => handleUserRedirect("auth/forgot-password")}
                variant="text"
                sx={{
                  color: "#658790",
                  fontSize: "12px",
                  fontWeight: "normal",
                  "&:hover": {
                    opacity: "0.8",
                    backgroundColor: "transparent",
                  },
                }}
              >
                ESQUECEU SUA SENHA?
              </Button>
            </Box>
          </FormikForm>
        )}
      </Formik>
    </Box>
  );
};

export default Login;
